import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import { ADMIN_PALETTE } from '../adminDesignSystem';
import { API } from './complianceShared';

export function UboTab({ headers }) {
  const [legal, setLegal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/legal-settings`, { headers });
        setLegal(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load legal settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [headers]);

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/compliance/ubo-pdf`, {
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ubo-declaration-allurexp.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('UBO PDF download failed');
    }
  };

  if (loading) return <p style={{ color: ADMIN_PALETTE.inkMuted, fontSize: 13 }}>Loading…</p>;
  if (!legal) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <p style={{ margin: 0, fontSize: 13, color: ADMIN_PALETTE.inkMuted }}>
        Beneficial-ownership snapshot for KYC / UBO records. This bilingual (GR/EN) PDF is what
        Viva, banks and auditors ask for. Update ownership in <strong>Legal Entity Settings</strong>.
      </p>
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          background: ADMIN_PALETTE.surface,
          border: `1px solid ${ADMIN_PALETTE.border}`,
          display: 'grid',
          gridTemplateColumns: '180px 1fr',
          gap: 10,
        }}
      >
        {[
          ['Legal name', legal.company_name_latin || legal.company_name],
          ['Γ.Ε.ΜΗ.', legal.gemi_number],
          ['ΑΦΜ', legal.vat_number],
          ['ΔΟΥ', legal.tax_office],
          ['Registered seat', legal.address_latin || legal.address],
          ['Administrator', legal.legal_representative],
        ].map(([k, v]) => (
          <React.Fragment key={k}>
            <div style={{ fontSize: 12, color: ADMIN_PALETTE.inkMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {k}
            </div>
            <div style={{ fontSize: 14, color: ADMIN_PALETTE.ink }}>{v || '—'}</div>
          </React.Fragment>
        ))}
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 14,
          background: ADMIN_PALETTE.surface,
          border: `1px solid ${ADMIN_PALETTE.border}`,
        }}
      >
        <div style={{ fontSize: 12, color: ADMIN_PALETTE.inkMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
          Beneficial owners
        </div>
        <pre style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontSize: 13.5, color: ADMIN_PALETTE.ink, whiteSpace: 'pre-wrap' }} data-testid="ubo-shareholders-preview">
{legal.shareholders || '[No shareholders on file — set in Legal Entity Settings]'}
        </pre>
      </div>

      <button
        type="button"
        onClick={download}
        data-testid="ubo-download-pdf"
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 18px',
          borderRadius: 12,
          border: 'none',
          background: ADMIN_PALETTE.brandRed,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <Download size={16} /> Download UBO Declaration PDF (bilingual)
      </button>
    </div>
  );
}
